import { useState, useMemo, useCallback } from 'react';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@/components/ui/tabs';
import { IngestionDateRangePicker } from './IngestionDateRangePicker';
import { IngestionContextFilters } from './IngestionContextFilters';
import { IngestionEventJourneys } from './IngestionEventJourneys';
import { IngestionLiveLogs } from './IngestionLiveLogs';
import { useIngestionState, type IngestionStateAdapter, type IngestionStateParams } from '../hooks/useIngestionState';
import type { IngestionFilter } from '@/lib/api/system/ingestion-observability';

interface EmbeddedIngestionViewProps {
  lockedFilters: Partial<IngestionFilter>;
}

const DEFAULT_PARAMS: IngestionStateParams = {
  projectId: null,
  videoProcessorConfigId: null,
  assetId: null,
  stage: null,
  outcome: null,
  type: null,
  correlationId: null,
  dateStart: null,
  dateEnd: null,
  tab: 'events',
  explorerPage: 1,
  explorerPageSize: 50,
  logWindowStart: null,
  logWindowEnd: null,
  liveTail: false,
  cameraName: null,
  pipelineView: 'journeys',
};

function useLocalAdapter(): IngestionStateAdapter {
  const [state, setState] = useState<IngestionStateParams>(DEFAULT_PARAMS);

  const get = useCallback(() => state, [state]);
  const set = useCallback(
    (updates: Partial<IngestionStateParams>) => {
      setState((prev) => ({ ...prev, ...updates }));
    },
    []
  );

  return useMemo(() => ({ get, set }), [get, set]);
}

export function EmbeddedIngestionView({ lockedFilters }: EmbeddedIngestionViewProps) {
  const adapter = useLocalAdapter();
  const [activeTab, setActiveTab] = useState<'events' | 'live'>('events');

  const {
    tenantId,
    filters,
    dateRange,
    dateRangeAsDate,
    setDateRange,
    setFilter,
    clearFilters,
    setParams,
    logWindow,
    canGoNewer,
    jumpToHistogramBucket,
    resetLogWindow,
    jumpAroundTime,
    extendDateRange,
  } = useIngestionState(adapter, lockedFilters);

  return (
    <Tabs
      value={activeTab}
      onValueChange={(t) => setActiveTab(t as 'events' | 'live')}
    >
      {/* Compact header: tabs + date picker */}
      <div className="flex items-center gap-3 border-b border-[#2E3835] px-4 py-2">
        <TabsList>
          <TabsTrigger value="events">Events</TabsTrigger>
          <TabsTrigger value="live">Live Logs</TabsTrigger>
        </TabsList>

        <div className="flex-1" />

        {activeTab === 'events' && (
          <IngestionDateRangePicker
            value={dateRangeAsDate}
            onChange={(start, end) => setDateRange(start, end)}
            onJumpAroundTime={(time) => jumpAroundTime(time)}
          />
        )}
      </div>

      <div className="px-4 py-3 space-y-3">
        <TabsContent value="events" className="mt-0 space-y-3">
          <IngestionContextFilters
            filters={filters}
            onFilterChange={(key, value) => setFilter(key, value)}
            onClear={() => { clearFilters(); setParams({ outcome: null }); }}
            mode="events"
            hideScope
          />
          <IngestionEventJourneys
            tenantId={tenantId}
            dateRange={dateRange}
            logWindow={logWindow}
            filters={filters}
            onJumpToHistogramBucket={jumpToHistogramBucket}
            onResetLogWindow={resetLogWindow}
            onExtendRange={extendDateRange}
            canGoNewer={canGoNewer}
          />
        </TabsContent>

        <TabsContent value="live" className="mt-0 space-y-3">
          <IngestionLiveLogs tenantId={tenantId} lockedFilters={lockedFilters} />
        </TabsContent>
      </div>
    </Tabs>
  );
}
